import { useEffect, useRef } from 'react'

import { type PageComponentProps } from '@typings/components/page'

import { usePageHeader } from '@contexts/page-header-context'

import { ContentWrapper } from '@components/ui-core/content-wrapper/content-wrapper'
import { HeaderType } from '@components/ui-core/header/header'

import { HeroBackgroundVideo } from '@custom/components/hero-background-video/hero-background-video'
import { PageLayout } from '@custom/components/page-layout/page-layout'
import { TrustedBy } from '@custom/components/trusted-by/trusted-by'

import { Banner } from './components/banner/banner'
import TabbedSection from './components/tabbed-section/tabbed-section'
import { homepageContent } from './content'
import { Hero } from './hero/hero'
import { ManifestoSection } from './manifesto-section/manifesto-section'
import { SuperhumanSection } from './superhuman-section/superhuman-section'

import styles from './page.module.css'

export default function HomePage({ data }: PageComponentProps) {
  const heroRef = useRef<HTMLElement | null>(null)
  const heroBackgroundVideoRef = useRef<HTMLVideoElement>(null)
  const { headerRef } = usePageHeader()

  // Helper function to handle video play/pause based on intersection ratio
  const handleVideoPlayback = (entry: IntersectionObserverEntry) => {
    const { intersectionRatio } = entry

    // Play when more than 50% visible, pause when less than 50% visible
    if (intersectionRatio > 0.5) {
      heroBackgroundVideoRef.current?.play()
    } else {
      heroBackgroundVideoRef.current?.pause()
    }
  }

  useEffect(() => {
    const supportsScrollAnimations = CSS.supports('animation-timeline', 'scroll()')

    if (supportsScrollAnimations) {
      // Animation timelines is supported! The animation happens via CSS
      // But we still need to pause/play the video based on visibility
      const heroSection = heroRef.current
      if (!heroSection) return

      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            handleVideoPlayback(entry)
          }
        },
        {
          // Check at 0%, 50%, and 100% visibility
          threshold: [0, 0.5, 1],
        },
      )

      observer.observe(heroSection)
      return () => observer.disconnect()
    }

    // Animation timelines is not supported! The animation happens via JavaScript
    const header = headerRef.current
    const heroSection = heroRef.current

    if (!header || !heroSection) return

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const { boundingClientRect } = entry

          // Handle video pause/play
          handleVideoPlayback(entry)

          // Get the hero section's current position and dimensions
          const heroTop = boundingClientRect.top // Distance from viewport top (negative when scrolled above)
          const heroHeight = boundingClientRect.height

          /*
            Calculate how many pixels of the hero have scrolled above the viewport
            - When heroTop is positive (hero visible below): scrolledAmount = 0
            - When heroTop is negative (hero scrolled up): scrolledAmount = absolute value of heroTop
          */
          const scrolledAmount = Math.max(0, -heroTop)

          /*
            Calculate what percentage of the hero has scrolled past the viewport top
            - scrollProgress ranges from 0 (hero fully visible) to 1 (hero completely scrolled away)
            - Example: if 500px of a 1000px hero has scrolled past, scrollProgress = 0.5
          */
          const scrollProgress = Math.min(1, scrolledAmount / heroHeight)

          /*
            Map scroll progress directly to header opacity
            - 0 = fully transparent header (hero is visible)
            - 1 = fully opaque header (hero has scrolled away)
          */
          const opacity = scrollProgress
          header.style.setProperty('--header-opacity', `${opacity * 100}%`)

          /*
            Remove blur effect only when header is fully transparent or fully opaque
            This adds blur during the transition
          */
          if (opacity === 0 || opacity >= 0.99) {
            header.style.setProperty('--header-has-blur', 'initial')
          } else {
            header.style.setProperty('--header-has-blur', '/* empty string */')
          }

          /*
            Switch color scheme when header is mostly opaque (80%+)
            Dark theme for transparent header (over hero), light theme for opaque header (under hero)
          */
          header.style.setProperty('color-scheme', opacity >= 0.6 ? 'light' : 'dark')
        }
      },
      {
        /*
          Check at every 1% change
          This ensures the header is updated smoothly as the hero scrolls
        */
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      },
    )

    observer.observe(heroSection)

    return () => {
      observer.disconnect()
    }
  }, [headerRef])

  return (
    <>
      <HeroBackgroundVideo ref={heroBackgroundVideoRef} className={styles.heroBackgroundVideo} />
      <PageLayout
        data={data}
        headerType={HeaderType.Global}
        banner={
          homepageContent?.banner?.content?.copy ? (
            <Banner
              text={homepageContent?.banner?.content?.copy}
              action={
                homepageContent?.banner?.content?.link
                  ? {
                      category: 'text',
                      url: homepageContent?.banner?.content?.link?.fields?.targetUrl ?? '',
                      text: homepageContent?.banner?.content?.link?.fields?.title ?? '',
                    }
                  : undefined
              }
            />
          ) : undefined
        }
        withFooter
        className={styles.homepage}
      >
        <Hero ref={heroRef} data={homepageContent.heroSection} className={styles.heroSection} />
        <div className={styles.multiSectionBackground}>
          <ContentWrapper className={styles.withVerticalBars}>
            <TrustedBy data={homepageContent?.trustedBySection} className={styles.trustedBy} />
            <TabbedSection data={homepageContent?.tabbedSection} />
          </ContentWrapper>
        </div>
        <ManifestoSection data={homepageContent.manifestoSection} />
        <SuperhumanSection data={homepageContent.superhumanSection} />
      </PageLayout>
    </>
  )
}
